/**
 * Canonical onboarding target identifiers.
 *
 * Targets are exposed in the live DOM as `data-onboarding-target="<id>"`
 * attributes. The mentor never selects via CSS classes — adding/changing
 * Tailwind classes does not break onboarding.
 */

export const ONBOARDING_TARGET_ATTR = "data-onboarding-target";

export const SHELL_TARGETS = {
  EDITOR_TOP_BAR: "editor-top-bar",
  SAVE_BUTTON: "editor-header:save",
  HISTORY_BUTTON: "editor-top-bar:history",
  SEO_BUTTON: "editor-top-bar:seo",
  PREVIEW_BUTTON: "editor-top-bar:preview",
  PUBLISH_BUTTON: "editor-top-bar:publish",
  VERSIONS_PANEL_ROOT: "panel:versions:root",
  PALETTE_HERO: "palette:category:hero",
  PALETTE_SOCIAL_PROOF: "palette:category:social-proof",
  PALETTE_CONTENT: "palette:category:content",
  PALETTE_CTA: "palette:category:cta",
  SEO_PANEL_ROOT: "panel:seo:root",
  CANVAS_FRAME: "canvas:iframe",
} as const;

export const DASHBOARD_TARGETS = {
  SIDEBAR_COCKPIT: "dashboard:sidebar:cockpit",
  SIDEBAR_LINK_OVERVIEW: "dashboard:sidebar:overview",
  SIDEBAR_LINK_WORKSPACE: "dashboard:sidebar:workspace",
  SIDEBAR_LINK_PAGES: "dashboard:sidebar:pages",
  SIDEBAR_LINK_FORMS: "dashboard:sidebar:forms",
  SIDEBAR_LINK_DEPLOYMENTS: "dashboard:sidebar:deployments",
  SIDEBAR_LINK_DOMAINS: "dashboard:sidebar:domains",
  SIDEBAR_LINK_TRANSLATION: "dashboard:sidebar:translation",
  SIDEBAR_LINK_GLOSSARY: "dashboard:sidebar:glossary",
  SIDEBAR_LINK_SEO: "dashboard:sidebar:seo",
  SIDEBAR_LINK_USAGE: "dashboard:sidebar:usage",
  SIDEBAR_LINK_SETTINGS: "dashboard:sidebar:settings",
  OVERVIEW_HEADER: "dashboard:overview:header",
  OVERVIEW_CHECKLIST: "dashboard:overview:checklist",
  WORKSPACE_HERO: "dashboard:workspace:hero",
  WORKSPACE_NEW_SITE: "dashboard:workspace:new-site",
  PAGES_HEADER: "dashboard:pages:header",
  PAGES_NEW_PAGE: "dashboard:pages:new-page",
  SETTINGS_HEADER: "dashboard:settings:header",
  SETTINGS_TAB_SEO: "dashboard:settings:tab:seo",
  SETTINGS_TAB_LOCALIZATION: "dashboard:settings:tab:localization",
  FORMS_HEADER: "dashboard:forms:header",
  DEPLOYMENTS_HEADER: "dashboard:deployments:header",
  DEPLOYMENTS_PUBLISH: "dashboard:deployments:publish",
  DOMAINS_HEADER: "dashboard:domains:header",
  DOMAINS_CONNECT: "dashboard:domains:connect",
  USAGE_HEADER: "dashboard:usage:header",
  USAGE_CHECKLIST: "dashboard:usage:checklist",
  TRANSLATION_HEADER: "dashboard:translation:header",
  GLOSSARY_HEADER: "dashboard:glossary:header",
  SEO_HEADER: "dashboard:seo:header",
} as const;

export type ShellTargetId =
  | (typeof SHELL_TARGETS)[keyof typeof SHELL_TARGETS]
  | (typeof DASHBOARD_TARGETS)[keyof typeof DASHBOARD_TARGETS]
  // Dynamic palette item targets, set by EditorPage's drawerItem override.
  // The literal string format is `palette:item:<componentName>` where
  // `<componentName>` matches the Puck registry key (e.g. "HeroSplit").
  | (string & { readonly __brand?: "DynamicTargetId" });

/**
 * Build the `data-onboarding-target` prop pair for use in JSX.
 * Example:  <button {...onboardingTarget("editor-top-bar:seo")} />
 */
export function onboardingTarget(id: ShellTargetId): Record<string, string> {
  return { [ONBOARDING_TARGET_ATTR]: id };
}

function isRenderableTargetCandidate(el: HTMLElement): boolean {
  const style = getComputedStyle(el);
  const rect = el.getBoundingClientRect();
  return (
    !el.hidden &&
    style.display !== "none" &&
    style.visibility !== "hidden" &&
    rect.width > 0 &&
    rect.height > 0
  );
}

function scoreTargetCandidate(el: HTMLElement): number {
  const style = getComputedStyle(el);
  const rect = el.getBoundingClientRect();
  const viewportCenterY = window.innerHeight / 2;
  const rectCenterY = rect.top + rect.height / 2;
  let score = 0;

  if (style.pointerEvents !== "none") score += 100;
  if (style.opacity !== "0") score += 10;
  if (rect.top < window.innerHeight && rect.bottom > 0) score += 5;

  return score - Math.abs(rectCenterY - viewportCenterY) / 1000;
}

export function findTargetElement(id: ShellTargetId): HTMLElement | null {
  if (typeof document === "undefined") return null;
  // Escape quotes defensively even though our IDs don't include them.
  const escaped = String(id).replace(/"/g, '\\"');
  const matches = Array.from(
    document.querySelectorAll<HTMLElement>(
      `[${ONBOARDING_TARGET_ATTR}="${escaped}"]`,
    ),
  );

  if (matches.length === 0) return null;

  const renderable = matches.filter(isRenderableTargetCandidate);
  if (renderable.length === 0) return null;

  renderable.sort((a, b) => scoreTargetCandidate(b) - scoreTargetCandidate(a));
  return renderable[0] ?? null;
}
